import { useState, useEffect } from "react";
import "@/App.css";
import "./elegant.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { FoodTruck } from "./pages/FoodTruck";
import { Menu } from "./pages/Menu";
import { Cart } from "./pages/Cart";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { AdminDashboard } from "./pages/AdminDashboard";
import { MyOrders } from "./pages/MyOrders";
import { CateringMenus } from "./pages/CateringMenus";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { Terms } from "./pages/Terms";

function ScrollToHashElement() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // Wait a tick so the target route has rendered
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [location.pathname, location.hash]);
  return null;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    // Skip admin / login — backend filters too, but this avoids the call
    if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/login")) return;

    // Anonymous per-browser session id, persisted across page loads
    let sessionId = localStorage.getItem("rc_session_id");
    if (!sessionId) {
      sessionId = (crypto.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2) + Date.now();
      localStorage.setItem("rc_session_id", sessionId);
    }

    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/page-view`, {
        path: location.pathname,
        session_id: sessionId,
        referrer: document.referrer || null,
      })
      .catch(() => { /* analytics failures must never disrupt UX */ });
  }, [location.pathname]);
  return null;
}

function AppContent() {
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    // Update cart count from localStorage
    const updateCartCount = () => {
      const savedCart = localStorage.getItem("cart");
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const count = cart.reduce((sum, item) => sum + item.quantity, 0);
        setCartItemsCount(count);
      }
    };

    updateCartCount();

    // Listen for storage changes
    window.addEventListener("storage", updateCartCount);
    
    // Poll for cart changes (since localStorage doesn't fire events in same tab)
    const interval = setInterval(updateCartCount, 1000);

    return () => {
      window.removeEventListener("storage", updateCartCount);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="App">
      <ScrollToHashElement />
      <PageViewTracker />
      <Navbar cartItemsCount={cartItemsCount} />
      <Routes>
        <Route path="/" element={<FoodTruck />} />
        <Route path="/food-truck" element={<Navigate to="/" replace />} />
        <Route path="/menu" element={<Navigate to="/" replace />} />
        <Route path="/meal-prep" element={<Menu />} />
        <Route path="/catering-menus" element={<CateringMenus />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cart" element={<Cart />} />
        <Route 
          path="/login" 
          element={user ? <Navigate to="/" replace /> : <Login />} 
        />
        <Route 
          path="/register" 
          element={user ? <Navigate to="/" replace /> : <Register />} 
        />
        <Route
          path="/my-orders"
          element={
            <ProtectedRoute>
              <MyOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
      </Routes>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
